import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronLeft, ChevronRight, Flag } from 'lucide-react';
import { z } from 'zod';
import { addDays, today, weekDays, weekStart, zonedDate, zonedDateTime, zonedMidnight, zonedMinutes } from '@mkcore/shared';
import { listMilestones } from '@mkcore/dev/schema';
import { listCalendarEvents, updateEntry, type CalendarEvent } from '@mkcore/time/schema';
import { Button, ErrorState, PageHeader, Skeleton, cn, toast } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDuration } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { EntryDialog, type EntryInitial, type TimeEntry } from '@/features/time/entry-dialog';
import { entriesQuery } from '@/features/time/queries';
import { hhmm, isoWeekNumber } from '@/features/time/util';

const search = z.object({ date: z.string().optional(), view: z.enum(['week', 'day']).optional() });
export const Route = createFileRoute('/_app/time/calendar')({ validateSearch: search, component: Calendar });

const PX_PER_HOUR = 48;
const SNAP = 15;
const DAY_PX = 24 * PX_PER_HOUR;
const snap = (minutes: number) => Math.max(0, Math.min(24 * 60, Math.round(minutes / SNAP) * SNAP));

interface Placed {
  start: number;
  end: number;
  lane: number;
  lanes: number;
  overlaps: boolean;
}
type Block = Placed & { entry: TimeEntry };
type CalEvent = z.output<typeof CalendarEvent>;
type EventBlock = Placed & { event: CalEvent };

/** The share of a day column for the M365 events when the day has any (CO-0251); the entries take the rest. */
const EVENT_SHARE = { week: 40, day: 30 } as const;

/** Positions the timed entries of one day in lanes, so overlapping blocks sit side by side and are marked (CO-0215). */
function layout(entries: TimeEntry[]): Block[] {
  return lanes(
    entries
      .filter((e) => e.timed)
      .map((e) => {
        const start = zonedMinutes(new Date(e.startedAt));
        return { entry: e, start, end: Math.min(24 * 60, start + Math.round(e.durationSeconds / 60)), lane: 0, lanes: 1, overlaps: false };
      }),
  );
}

/** The timed M365 events of one day, cut to the day, in lanes of their own. */
function eventLayout(events: CalEvent[], day: string): EventBlock[] {
  const dayStart = zonedMidnight(day).getTime();
  const dayEnd = zonedMidnight(addDays(day, 1)).getTime();
  return lanes(
    events
      .filter((e) => !e.allDay && Date.parse(e.startedAt) < dayEnd && Date.parse(e.endedAt) > dayStart)
      .map((e) => {
        const start = Date.parse(e.startedAt) <= dayStart ? 0 : zonedMinutes(new Date(e.startedAt));
        const end = Date.parse(e.endedAt) >= dayEnd ? 24 * 60 : zonedMinutes(new Date(e.endedAt));
        return { event: e, start, end: Math.max(end, start + SNAP), lane: 0, lanes: 1, overlaps: false };
      }),
  );
}

/** An all-day event runs from midnight to midnight UTC, as Graph gives it: it covers the days in between. */
const allDayOn = (e: CalEvent, day: string) => e.allDay && e.startedAt.slice(0, 10) <= day && day < e.endedAt.slice(0, 10);

function lanes<T extends Placed>(input: T[]): T[] {
  const items = input.sort((a, b) => a.start - b.start || a.end - b.end);
  let cluster: T[] = [];
  let clusterEnd = -1;
  const close = () => {
    const lanes = Math.max(1, ...cluster.map((b) => b.lane + 1));
    for (const b of cluster) {
      b.lanes = lanes;
      b.overlaps = cluster.length > 1;
    }
    cluster = [];
  };
  for (const b of items) {
    if (b.start >= clusterEnd) close();
    const taken = new Set(cluster.filter((c) => c.end > b.start).map((c) => c.lane));
    let lane = 0;
    while (taken.has(lane)) lane++;
    b.lane = lane;
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, b.end);
  }
  close();
  return items;
}

type Drag =
  | { kind: 'create'; day: string; anchor: number; current: number }
  | { kind: 'move'; entry: TimeEntry; day: string; originDay: string; grabOffset: number; start: number; duration: number; moved: boolean }
  | { kind: 'resize'; entry: TimeEntry; day: string; start: number; end: number };

/** Day and week calendar: blocks per entry, drag on empty space to create, drag a block to move, its edge to extend (CO-0227). */
function Calendar() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canCreate = useCan('time:create');
  const startsOn = useMe().weekStart;
  const view = s.view ?? 'week';
  const anchorDate = s.date ?? today();
  const days = view === 'week' ? weekDays(weekStart(anchorDate, startsOn)) : [anchorDate];
  const q = useInfiniteQuery(entriesQuery({ from: days[0]!, to: days[days.length - 1]!, limit: 200 }));
  const entries = q.data?.pages.flatMap((p) => p.items) ?? [];
  const update = useActionMutation(updateEntry, { invalidate: [['time']], silentErrors: true });
  // The M365 calendar next to the hours (CO-0251), and milestones that fall due: what else has a date that week.
  const from = days[0]!;
  const to = days[days.length - 1]!;
  const calendar = useQuery({ queryKey: ['time', 'calendarEvent', 'list', from, to], queryFn: () => call(listCalendarEvents, { from, to }), retry: false, staleTime: 60_000 });
  const [showEvents, setShowEvents] = useState(true);
  const events = calendar.data?.available && showEvents ? calendar.data.items : [];
  const canDev = useCan('dev:read');
  const milestones = useQuery({ queryKey: ['dev', 'milestone', 'list', 'open'], queryFn: () => call(listMilestones, { state: 'open' }), enabled: canDev, staleTime: 60_000 });
  const dueOn = (d: string) => (milestones.data?.items ?? []).filter((m) => m.dueOn && zonedDate(new Date(m.dueOn)) === d);
  const [dialog, setDialog] = useState<{ open: boolean; entry: TimeEntry | null; init?: EntryInitial }>({ open: false, entry: null });
  const [drag, setDrag] = useState<Drag | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.scrollTo({ top: 7 * PX_PER_HOUR });
  }, []);
  const go = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const step = view === 'week' ? 7 : 1;

  const minutesAt = (el: HTMLElement, clientY: number) => snap(((clientY - el.getBoundingClientRect().top) / PX_PER_HOUR) * 60);
  const dayAt = (clientX: number): string | null => {
    const cols = scroller.current?.querySelectorAll<HTMLElement>('[data-day]') ?? [];
    for (const c of cols) {
      const r = c.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) return c.dataset.day!;
    }
    return null;
  };

  const onColumnDown = (e: ReactPointerEvent<HTMLDivElement>, day: string) => {
    if (!canCreate || e.button !== 0 || (e.target as HTMLElement).closest('[data-block]')) return;
    const m = minutesAt(e.currentTarget, e.clientY);
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ kind: 'create', day, anchor: m, current: m });
  };
  const onBlockDown = (e: ReactPointerEvent<HTMLDivElement>, b: Block, day: string, resize: boolean) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!canCreate || b.entry.locked) {
      setDialog({ open: true, entry: b.entry });
      return;
    }
    const column = e.currentTarget.closest<HTMLElement>('[data-day]')!;
    column.setPointerCapture(e.pointerId);
    const m = minutesAt(column, e.clientY);
    if (resize) setDrag({ kind: 'resize', entry: b.entry, day, start: b.start, end: b.end });
    else setDrag({ kind: 'move', entry: b.entry, day, originDay: day, grabOffset: m - b.start, start: b.start, duration: b.end - b.start, moved: false });
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const column = e.currentTarget;
    const m = minutesAt(column, e.clientY);
    if (drag.kind === 'create') setDrag({ ...drag, current: m });
    else if (drag.kind === 'resize') setDrag({ ...drag, end: Math.max(drag.start + SNAP, m) });
    else {
      const day = dayAt(e.clientX) ?? drag.day;
      const start = Math.max(0, Math.min(24 * 60 - drag.duration, m - drag.grabOffset));
      setDrag({ ...drag, day, start, moved: drag.moved || start !== drag.start || day !== drag.day });
    }
  };
  const commit = (d: Drag) => {
    setDrag(null);
    if (d.kind === 'create') {
      const a = Math.min(d.anchor, d.current);
      const b = Math.max(d.anchor, d.current);
      setDialog({ open: true, entry: null, init: { date: d.day, startMinutes: a, endMinutes: b - a < SNAP ? a + 60 : b } });
      return;
    }
    if (d.kind === 'move') {
      if (!d.moved) {
        setDialog({ open: true, entry: d.entry });
        return;
      }
      const startedAt = zonedDateTime(d.day, d.start).toISOString();
      const endedAt = new Date(Date.parse(startedAt) + d.duration * 60_000).toISOString();
      update.mutate({ input: { id: d.entry.id, startedAt, endedAt }, ifMatch: d.entry.version }, { onError: (err) => toast.error(message(err)) });
      return;
    }
    const startedAt = zonedDateTime(d.day, d.start).toISOString();
    const endedAt = zonedDateTime(d.day, d.end).toISOString();
    update.mutate({ input: { id: d.entry.id, startedAt, endedAt }, ifMatch: d.entry.version }, { onError: (err) => toast.error(message(err)) });
  };

  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const todayDate = today();
  const dayName = (d: string) => t(`workSettings.day.${new Date(`${d}T00:00:00Z`).getUTCDay() || 7}`);
  const label =
    view === 'week'
      ? `${t('time.week.weekLabel', { week: isoWeekNumber(days[0]!) })} · ${days[0]!.slice(8)}–${days[6]!.slice(8)}-${days[6]!.slice(5, 7)}-${days[6]!.slice(0, 4)}`
      : `${dayName(anchorDate)} ${anchorDate.slice(8)}-${anchorDate.slice(5, 7)}-${anchorDate.slice(0, 4)}`;

  return (
    <>
      <PageHeader
        title={t('time.calendar.title')}
        description={t('time.calendar.description')}
        actions={
          <div className="flex gap-1 rounded-md border border-line p-0.5">
            <Button variant={view === 'day' ? 'secondary' : 'ghost'} size="sm" onClick={() => go({ view: 'day' })} aria-pressed={view === 'day'}>
              {t('time.calendar.day')}
            </Button>
            <Button variant={view === 'week' ? 'secondary' : 'ghost'} size="sm" onClick={() => go({ view: 'week' })} aria-pressed={view === 'week'}>
              {t('time.calendar.week')}
            </Button>
          </div>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" aria-label={t('time.calendar.previous')} onClick={() => go({ date: addDays(anchorDate, -step) })}>
          <ChevronLeft />
        </Button>
        <span className="min-w-56 text-sm font-bold">{label}</span>
        <Button variant="ghost" size="icon" aria-label={t('time.calendar.next')} onClick={() => go({ date: addDays(anchorDate, step) })}>
          <ChevronRight />
        </Button>
        <Button variant="secondary" size="sm" onClick={() => go({ date: undefined })} disabled={anchorDate === todayDate}>
          {t('time.today')}
        </Button>
        {calendar.data?.available && (
          <Button variant={showEvents ? 'secondary' : 'ghost'} size="sm" aria-pressed={showEvents} onClick={() => setShowEvents((v) => !v)}>
            <CalendarDays /> {t('time.calendar.m365')}
          </Button>
        )}
        {calendar.data?.unavailable === 'failed' && <span className="text-xs text-danger">{t('time.calendar.m365Failed', { detail: calendar.data.detail ?? '' })}</span>}
        <span className="ml-auto text-xs text-fg-3">{t('time.calendar.hint')}</span>
      </div>
      {q.isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-line-subtle bg-surface">
          <div className="grid border-b border-line-subtle" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}>
            <div />
            {days.map((d) => {
              const untimed = entries.filter((e) => e.date === d && !e.timed);
              const total = entries.filter((e) => e.date === d).reduce((a, e) => a + e.durationSeconds, 0);
              return (
                <div key={d} className={cn('border-l border-line-subtle px-2 py-1', d === todayDate && 'bg-gold/5')}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className={cn('font-bold uppercase tracking-caps', d === todayDate ? 'text-gold-strong' : 'text-fg-2')}>
                      {dayName(d)} {d.slice(8)}
                    </span>
                    <span className="tabular-nums text-fg-3">{total ? formatDuration(total) : ''}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {dueOn(d).map((m) => (
                      <Link
                        key={m.id}
                        to="/dev/milestones/$id"
                        params={{ id: m.id }}
                        className="inline-flex max-w-full items-center gap-0.5 truncate rounded-sm border border-line px-1 text-xs text-fg-2 hover:underline"
                        title={t('time.calendar.milestoneDue', { title: m.title })}
                      >
                        <Flag className="size-3 shrink-0" aria-hidden /> {m.title}
                      </Link>
                    ))}
                    {events
                      .filter((e) => allDayOn(e, d))
                      .map((e) => (
                        <span key={e.id} className="max-w-full truncate rounded-sm border border-info bg-info-bg px-1 text-xs text-info" title={e.subject}>
                          {e.subject}
                        </span>
                      ))}
                    {untimed.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        className="max-w-full truncate rounded-sm px-1 text-xs text-white"
                        style={{ backgroundColor: e.projectColour }}
                        title={`${e.projectName} · ${e.description}`}
                        onClick={() => setDialog({ open: true, entry: e })}
                      >
                        {formatDuration(e.durationSeconds)} {e.description || e.projectName}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div ref={scroller} data-testid="calendar-scroll" className="relative max-h-[70vh] overflow-y-auto">
            <div className="grid" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))`, height: DAY_PX }}>
              <div className="relative">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="absolute right-1 -translate-y-1/2 text-[10px] tabular-nums text-fg-4" style={{ top: h * PX_PER_HOUR }}>
                    {h ? `${String(h).padStart(2, '0')}:00` : ''}
                  </div>
                ))}
              </div>
              {days.map((d) => {
                const blocks = layout(entries.filter((e) => e.date === d));
                const eventBlocks = eventLayout(events, d);
                const share = eventBlocks.length ? EVENT_SHARE[view] : 0;
                const ghost =
                  drag && drag.day === d
                    ? drag.kind === 'create'
                      ? { start: Math.min(drag.anchor, drag.current), end: Math.max(drag.anchor, drag.current) || drag.anchor + SNAP }
                      : drag.kind === 'move'
                        ? { start: drag.start, end: drag.start + drag.duration }
                        : { start: drag.start, end: drag.end }
                    : null;
                return (
                  <div
                    key={d}
                    data-day={d}
                    className={cn('relative border-l border-line-subtle', d === todayDate && 'bg-gold/5', canCreate && 'cursor-crosshair')}
                    onPointerDown={(e) => onColumnDown(e, d)}
                    onPointerMove={onMove}
                    onPointerUp={() => drag && commit(drag)}
                    onPointerCancel={() => setDrag(null)}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <div key={h} className="absolute inset-x-0 border-t border-line-subtle" style={{ top: h * PX_PER_HOUR }} />
                    ))}
                    {eventBlocks.map((b) => (
                      <button
                        key={b.event.id}
                        type="button"
                        data-block
                        aria-label={t('time.calendar.event', { from: hhmm(b.start), to: hhmm(b.end), subject: b.event.subject })}
                        title={[`${hhmm(b.start)}–${hhmm(b.end)} ${b.event.subject}`, b.event.location, b.event.draftStatus && t(`time.calendar.draft.${b.event.draftStatus}`)].filter(Boolean).join(' · ')}
                        className={cn(
                          'absolute overflow-hidden rounded-sm border px-1 text-left text-xs text-info',
                          b.event.showAs === 'free' || b.event.showAs === 'tentative' ? 'border-dashed border-info/60 bg-surface' : 'border-info bg-info-bg',
                          canCreate ? 'cursor-pointer hover:brightness-95' : 'cursor-default',
                        )}
                        style={{
                          top: (b.start / 60) * PX_PER_HOUR,
                          height: Math.max(14, ((b.end - b.start) / 60) * PX_PER_HOUR - 2),
                          left: `calc(${(b.lane / b.lanes) * share}% + 2px)`,
                          width: `calc(${share / b.lanes}% - 4px)`,
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => canCreate && setDialog({ open: true, entry: null, init: { date: d, startMinutes: b.start, endMinutes: b.end, description: b.event.subject } })}
                      >
                        <div className="truncate font-bold">{b.event.subject}</div>
                        {b.event.draftStatus && <div className="truncate opacity-80">{t(`time.calendar.draft.${b.event.draftStatus}`)}</div>}
                      </button>
                    ))}
                    {blocks.map((b) => {
                      const hidden = drag?.kind === 'move' && drag.entry.id === b.entry.id && drag.moved;
                      return (
                        <div
                          key={b.entry.id}
                          data-block
                          role="button"
                          tabIndex={0}
                          aria-label={`${hhmm(b.start)}–${hhmm(b.end)} ${b.entry.projectName} ${b.entry.description}`}
                          className={cn(
                            'absolute overflow-hidden rounded-sm border border-white/40 px-1 text-xs text-white shadow-sm',
                            b.overlaps && 'border-2 border-dashed border-danger',
                            b.entry.locked ? 'cursor-not-allowed opacity-70' : 'cursor-grab',
                            hidden && 'opacity-30',
                          )}
                          style={{
                            top: (b.start / 60) * PX_PER_HOUR,
                            height: Math.max(14, ((b.end - b.start) / 60) * PX_PER_HOUR - 2),
                            left: `calc(${share + (b.lane / b.lanes) * (100 - share)}% + 2px)`,
                            width: `calc(${(100 - share) / b.lanes}% - 4px)`,
                            backgroundColor: b.entry.projectColour,
                          }}
                          onPointerDown={(e) => onBlockDown(e, b, d, false)}
                          onKeyDown={(e) => e.key === 'Enter' && setDialog({ open: true, entry: b.entry })}
                        >
                          <div className="truncate font-bold">{b.entry.description || b.entry.projectName}</div>
                          <div className="truncate opacity-90">
                            {hhmm(b.start)}–{hhmm(b.end)} · {b.entry.projectName}
                          </div>
                          {!b.entry.locked && <div className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize" onPointerDown={(e) => onBlockDown(e, b, d, true)} />}
                        </div>
                      );
                    })}
                    {ghost && (
                      <div
                        className="pointer-events-none absolute inset-x-0.5 rounded-sm border-2 border-dashed border-gold-strong bg-gold/20 px-1 text-xs"
                        style={{ top: (ghost.start / 60) * PX_PER_HOUR, height: Math.max(8, ((ghost.end - ghost.start) / 60) * PX_PER_HOUR) }}
                      >
                        {hhmm(ghost.start)}–{hhmm(ghost.end)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <EntryDialog open={dialog.open} entry={dialog.entry} init={dialog.init ?? { date: anchorDate }} onClose={() => setDialog((d) => ({ ...d, open: false }))} />
    </>
  );
}
